<script lang="ts" setup>
import { ImgComparisonSlider } from '@img-comparison-slider/vue'

const props = defineProps<{
  data: {
    id: number
    translations: DirectusTranslation<{
      title: string
      section_title: string
    }>
    items: {
      id: number
      block_pool_palette_item_id: {
        id: number
        color_selector: string
        pool_filled: string
        pool_empty: string
        translations: DirectusTranslation<{
          color_name: string
        }>
      }
    }[]
  }
}>()

const { dt } = useDirectusTranslation()
const { hyphen } = useHyphen()

const items = computed(() => {
  return props.data.items.map((item) => {
    return {
      ...item.block_pool_palette_item_id,
      ...dt(item.block_pool_palette_item_id.translations),
      translations: undefined,
    }
  })
})

const activeItemIndex = ref(0)

const activeItemIndexString = computed({
  get: () => activeItemIndex.value.toString(),
  set: (value) => {
    activeItemIndex.value = Number.parseInt(value)
  },

})

const activeItem = computed(() => {
  return items.value[activeItemIndex.value]
})
</script>

<template>
  <BlockBase is-light class="bg-neutral-white py-15rem text-neutral-black">
    <Container class="grid grid-cols-1 w-full gap-10rem lg:grid-cols-2">
      <!-- Color selector -->
      <div class="my-auto w-full">
        <h1 class="mb-4rem text-balance textH1Black">
          {{ hyphen(dt(props.data.translations)?.title) }}
        </h1>
        <TextWithAfterLine
          v-if="dt(props.data.translations)?.section_title" class="mb-2rem"
          :text="dt(props.data.translations)?.section_title ?? ''"
        />
        <div class="mt-8rem">
          <RadixRadioGroupRoot v-model="activeItemIndexString">
            <div class="w-full flex flex-wrap gap-2rem">
              <RadixRadioGroupItem
                v-for="item, index in items" :key="index" :value="index.toString()"
                class="inline-block cursor-pointer select-none overflow-hidden rounded-full shadow-none ring-0 ring-neutral-darkGray ring-offset-neutral-black transition duration-300ms data-[active=true]:shadow-starline data-[active=true]:ring-3 data-[active=true]:ring-offset-2 data-[active=true]:ring-starline-primary hover:ring-starline-primary"
              >
                <NuxtImg loading="lazy" provider="directus" height="30" width="30" :src="item.color_selector" :alt="item.color_name" />
              </RadixRadioGroupItem>
            </div>
          </RadixRadioGroupRoot>
        </div>
      </div>

      <!-- IMAGE COMPARE -->
      <div class="my-auto w-full">
        <ImgComparisonSlider value="33">
          <!-- eslint-disable -->
          <NuxtImg provider="directus" :height="450" slot="first" class="comparison-image" style="width: 100%" :src="activeItem?.pool_filled" loading="lazy" />
          <NuxtImg provider="directus" :height="450" slot="second" class="comparison-image" style="width: 100%" :src="activeItem?.pool_empty" loading="lazy" />
          <svg slot="handle" class="size-4rem" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 40 40"><path fill="#000B1A" d="M0 0h40v40H0z"/><path fill="#fff" d="M10.47 20.2c0 .21.08.42.23.57l3.94 3.94a.82.82 0 0 0 1.17-1.16l-3.36-3.36 3.36-3.36a.82.82 0 1 0-1.17-1.16l-3.94 3.94a.82.82 0 0 0-.23.59ZM29.53 20.2c0 .21-.08.42-.23.57l-3.94 3.94a.82.82 0 0 1-1.17-1.16l3.36-3.36-3.36-3.36a.82.82 0 1 1 1.17-1.16l3.94 3.94c.15.16.24.37.23.59Z"/></svg>
          <!-- eslint-enable -->
        </ImgComparisonSlider>
        <p class="relative mt-1rem whitespace-nowrap text-right text-1.1rem text-neutral-gray tracking-3px uppercase">
          {{ activeItem?.color_name }}
        </p>
      </div>
    </Container>
  </BlockBase>
</template>

<style scoped lang="scss">
img-comparison-slider {
  --divider-width: 1px;
  --divider-color: rgb(0 11 26);
  @apply border border-starline-secondary w-full;
}

.comparison-image {
  @apply w-full h-45rem object-contain;
}
</style>
